import { SERVICES } from 'utils/service-entitlements-config';
import { NavLeafConfig, NavSectionConfig } from './types';

/**
 * Declarative source of truth for the sidebar's accordion sections (PI-11617).
 *
 * Section membership and order come from the Figma "Accordion sidebar" page
 * (file ZzAENe29qdWp314zf20n15); items Figma doesn't picture are placed where
 * the Angular app placed them (apps/igww/src/app/navigation/menu-items.ts).
 * Labels and routes are unchanged from the pre-accordion React sidebar.
 *
 * Section rows are expand/collapse triggers only — they never navigate.
 * Children carry no icon (Figma); only section rows do.
 *
 * `Medical` leads the list because the medical brand's frames show it above
 * Identity. For every other brand the section drops out entirely once the
 * `medicalDataMonitoring` entitlement is absent, so no brand check is needed.
 */
export const NAV_SECTIONS: NavSectionConfig[] = [
  {
    key: 'medical',
    label: 'Medical',
    icon: 'medical_data',
    items: [
      {
        key: 'medical-data-monitoring',
        label: 'Medical Data Monitoring',
        navigateTo: '/medical-data-monitoring',
        service: SERVICES.medicalDataMonitoring,
      },
    ],
  },
  {
    key: 'identity',
    label: 'Identity',
    icon: 'userProtected',
    items: [
      {
        key: 'dark-web',
        label: 'Dark Web Watchlist',
        navigateTo: '/dark-web',
        service: SERVICES.darkweb,
      },
      {
        key: 'dark-web-scan-report',
        label: 'Dark Web Scan Report',
        navigateTo: '/dark-web-scan-report',
        service: SERVICES.darkwebScanReport,
        external: true,
      },
      {
        key: 'social-media-monitoring',
        label: 'Social Media Monitoring',
        navigateTo: '/social-media-monitoring',
        service: SERVICES.socialMedia,
      },
      {
        // Public Record Trace — Minor Defense-only feature. The
        // `publicRecordTrace` service is client-synthesised in
        // `selectUserFeatures` for every dminor user (see
        // `services/session/sessionSelectors.ts`), so gating the menu
        // item here mirrors the dashboard card's `hasService(SERVICES.
        // publicRecordTrace)` gate — link shows iff the card would.
        key: 'public-record-trace',
        label: 'Public Record Trace',
        navigateTo: '/public-record-trace',
        service: SERVICES.publicRecordTrace,
      },
      {
        // Angular parity — `menu-items.ts:69-74` (Identity section,
        // `forFeature: authentication_alerts`).
        key: 'identity-monitoring',
        label: 'Identity Monitoring',
        navigateTo: '/identity/identity-monitoring',
        service: SERVICES.identityMonitoring,
      },
      {
        /* Angular parity — Identity section link for SSN Trace
         * Report. Gated on the same entitlement the component uses. */
        key: 'ssn-trace-report',
        label: 'SSN Trace Report',
        navigateTo: '/identity/ssn-trace-report',
        service: SERVICES.ssnTraceReport,
      },
    ],
  },
  {
    key: 'credit',
    label: 'Credit',
    icon: 'credit-report',
    items: [
      {
        key: 'credit-score',
        label: 'Credit Score',
        navigateTo: '/credit-score',
        service: SERVICES.creditScore,
      },
      {
        key: 'credit-tracker',
        label: 'Credit Tracker',
        navigateTo: '/credit-tracker',
        service: SERVICES.creditScore,
      },
      {
        key: 'credit-report',
        label: 'Credit Report',
        navigateTo: '/credit-report',
        service: SERVICES.creditReport,
      },
      {
        key: 'credit-lock',
        label: 'Credit Lock',
        navigateTo: '/credit-lock',
        service: SERVICES.creditLock,
      },
      {
        key: 'security-freeze',
        label: 'Security Freeze Assist',
        navigateTo: '/resources/security-freeze/home',
        service: SERVICES.securityFreeze,
      },
      {
        key: 'credit-score-simulator',
        label: 'Credit Score Simulator',
        navigateTo: '/credit/score-simulator',
        service: SERVICES.creditScoreSimulator,
      },
    ],
  },
  {
    key: 'financial',
    label: 'Financial',
    icon: 'dollar_circle',
    items: [
      {
        key: 'financial/transaction-monitoring',
        label: 'Transaction Monitoring',
        navigateTo: '/financial/transaction-monitoring/transaction-history',
        highlightPath: '/financial/transaction-monitoring',
        service: SERVICES.txmon,
      },
      {
        key: 'spending-tracker',
        label: 'Spending Tracker',
        navigateTo: '/financial/spending',
        service: SERVICES.spendingTracker,
      },
      {
        key: 'bank-account-monitoring',
        label: 'Bank Account Monitoring',
        navigateTo: '/bank-account-monitoring',
        service: SERVICES.bankAccountMonitoring,
      },
      {
        key: 'home-title-monitoring',
        label: 'Home Title Monitoring',
        navigateTo: '/financial/new-home-title-monitoring',
        service: SERVICES.htmon,
      },
      // Distinct feature from the entry above (public records vs. home title
      // guardian), sharing the same label. Both are kept: entitlement logic is
      // out of scope for PI-11617 and Angular exposes them the same way.
      {
        key: 'public-records-monitoring',
        label: 'Home Title Monitoring',
        navigateTo: '/financial/home-title-monitoring',
        service: SERVICES.publicRecords,
      },
      {
        key: 'wallet-protection',
        label: 'Wallet Protection',
        navigateTo: '/wallet-protection',
        service: SERVICES.walletProtection,
      },
      {
        key: 'alternative-loan-report',
        label: 'Alternative Loan Report',
        navigateTo: '/financial/alternative-loan-report',
        service: SERVICES.alternativeLoanReport,
      },
      {
        key: 'financial-calculator',
        label: 'Financial Calculators',
        // Route is plural (`routes.tsx` → `path: 'financial-calculators'`), but
        // the QA selector stays on the singular label it shipped with, so
        // existing automation keeps matching `sidebar_financial-calculator`.
        qaLabel: 'Financial Calculator',
        navigateTo: '/financial/financial-calculators',
        service: SERVICES.financialCalculator,
      },
    ],
  },
  {
    key: 'privacy',
    label: 'Privacy',
    icon: 'hand_inactive',
    items: [
      {
        key: 'data-broker-opt-out',
        label: 'Data Broker Opt Out',
        navigateTo: '/data-broker-opt-out',
        service: SERVICES.dboo,
      },
      {
        key: 'password-scan',
        label: 'Password Scan',
        navigateTo: '/password-scan',
        service: SERVICES.passwordScan,
      },
    ],
  },
  {
    key: 'security',
    label: 'Security',
    icon: 'device-protection',
    items: [
      {
        key: 'password-defense',
        label: 'Password Defense',
        navigateTo: '/password-defense',
        service: SERVICES.passwordDefense,
      },
      {
        key: 'password-manager',
        label: 'Password Manager',
        navigateTo: '/security/password-manager',
        service: SERVICES.passwordManager,
      },
      {
        key: 'vpn-in-touch',
        label: 'VPN in Touch',
        navigateTo: '/vpn-in-touch',
        service: SERVICES.vpnInTouch,
      },
      {
        key: 'passwords',
        label: 'Passwords',
        navigateTo: '/passwords',
        service: SERVICES.passwords,
      },
      {
        key: 'digital-vault',
        label: 'Digital Vault',
        navigateTo: '/digital-vault',
        service: SERVICES.digitalVault,
      },
      {
        key: 'antivirus',
        label: 'UltraAV',
        navigateTo: '/security/antivirus',
        service: SERVICES.antivirus,
      },
    ],
  },
];

/** Every leaf across every section. */
const ALL_LEAVES: NavLeafConfig[] = NAV_SECTIONS.flatMap(
  (section) => section.items,
);

/**
 * Family-plan child-scoped routes reached by drilling into a child on the
 * My Family page. Any URL matching one of these belongs to "My Family" —
 * not the feature the URL is nested under — so:
 *   • antd's `selectedKeys` must point at the My Family top-level row
 *     (`plan-members`) and NOT at the section leaf whose path is a prefix
 *     of the current URL (Social Media Monitoring, Dark Web Watchlist,
 *     Alerts). Without this override, longest-prefix match auto-picks the
 *     leaf and antd auto-opens its accordion section.
 *   • `findSectionKeyForPath` must return `undefined` for the same reason
 *     — no accordion should expand.
 * `/my-account/plan-members/:childId` (protection details) doesn't need
 * an entry here: its natural prefix already matches the My Family row.
 */
const CHILD_INNER_PAGE_PATTERNS = [
  /^\/alerts\/child\/[^/]+/,
  /^\/dark-web\/child\/[^/]+/,
  /^\/social-media-monitoring\/[^/]+/,
];

export const PLAN_MEMBERS_LEAF_KEY = 'plan-members';

export const PLAN_MEMBERS_PATH = '/my-account/plan-members';

export function isChildInnerPage(pathname: string): boolean {
  return CHILD_INNER_PAGE_PATTERNS.some((re) => re.test(pathname));
}

/** True on the My Family list page and child protection-details routes. */
export function isPlanMembersMenuPath(pathname: string): boolean {
  return (
    pathname === PLAN_MEMBERS_PATH ||
    pathname.startsWith(`${PLAN_MEMBERS_PATH}/`)
  );
}

/**
 * True when the mobile drawer / avatar popover "My account" row should
 * highlight. Excludes plan-members — that route belongs to "My Family".
 */
export function isMyAccountMenuPath(pathname: string): boolean {
  if (pathname === '/my-account') return true;
  if (!pathname.startsWith('/my-account/')) return false;
  return !isPlanMembersMenuPath(pathname);
}

function matchLength(pathname: string, leafPath: string): number {
  if (pathname === leafPath) return leafPath.length;
  return pathname.startsWith(`${leafPath}/`) ? leafPath.length : 0;
}

/**
 * Longest-prefix match of the current pathname against each leaf's highlight
 * path, so detail routes (e.g. `/credit-report/:bureauId`) still highlight
 * their parent leaf.
 */
export function findActiveLeafKey(pathname: string): string | undefined {
  if (isChildInnerPage(pathname)) return PLAN_MEMBERS_LEAF_KEY;
  if (isPlanMembersMenuPath(pathname)) return PLAN_MEMBERS_LEAF_KEY;

  let bestKey: string | undefined;
  let bestLength = 0;

  ALL_LEAVES.forEach((leaf) => {
    if (leaf.external) return;
    const length = matchLength(pathname, leaf.highlightPath ?? leaf.navigateTo);
    if (length > bestLength) {
      bestLength = length;
      bestKey = leaf.key;
    }
  });

  return bestKey;
}

/** The section that owns the current pathname, so it can open on load. */
export function findSectionKeyForPath(pathname: string): string | undefined {
  // Child-inner routes belong to the flat "My Family" row, not to any
  // accordion section — collapse everything.
  if (isChildInnerPage(pathname)) return undefined;

  const activeLeafKey = findActiveLeafKey(pathname);
  if (!activeLeafKey) return undefined;

  const owner = NAV_SECTIONS.find((section) =>
    section.items.some((item) => item.key === activeLeafKey),
  );

  return owner?.key;
}
